/* eslint-disable no-shadow */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper, PDFManagerFactory } from 'pdftron';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import store from 'store/store';
import {
  AnnotationCustomData,
  CropZoneCustomData,
  CtfPdfBox,
  DocumentTypes,
  InputAnnotation,
  ManualAlignZoneCustomData,
  PDFTronTools,
  Quad,
} from 'types';
import type { BoundingBox } from '@verify/shared-utils';
import { app, inspection } from 'store';
import ToolsMixin from '../Tools';
import drawMarkupLabel from './utils/drawMarkupLabel';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from './utils/annotationConstants';
import { getPageRotation } from './utils/labelPositionUtils';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import {
  CursorStyle,
  cursorIsInsideCropZone,
  getCursorPageNumber,
  getAllPageCropZones,
  getAllPageManualAlignZones,
  getAllPageRectangularCropZones,
  preferredRectangularZone,
  shouldTakeHoverSelection,
  getIframeDocument,
  resetCursors,
  setCursorStyle,
  isRectangularCrop,
  cropContainsAnnotation,
  updateCropAndPolygonOverlays,
  getAllPageCropAndPolygonZones,
} from './utils/CropToolUtils';
import { getAllPagePolygonZones, pointInPolygon } from './utils/PolygonToolUtils';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { CrosshairManager } from './utils/CrosshairManager';
import quadsToDimensions from '../utils/quadsToDimensions';
import { getPageRanges } from './utils/getPageRanges';
import { startShapesJob } from 'store/request/files/actions';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { drawAnchorIcon } from './ManualAlignZone';

export interface SerializedCropZoneAnnotation {
  pageNumber: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  cropId?: string;
}

class CropZone {
  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get toolsMixin() {
    return new ToolsMixin(this.source, this.target);
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  createCropZoneAnnotation(instance: WebViewerInstance) {
    class CropZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        const state = store.getState();
        this.setCustomData(CropZoneCustomData.cropZoneAnnotation, 'true');

        this.ToolName = PDFTronTools.CROP;
        this.IsHoverable = true;
        this.disableRotationControl();
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const { DEFAULT_FILL_STYLE, SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);

        const state = store.getState();
        const manualAlignMode = state.inspection.manualAlignMode;
        const isInManualAlignMode = manualAlignMode.isActive && manualAlignMode.cropZoneId === this.Id;
        if (isInManualAlignMode) {
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 2;
        } else {
          ctx.strokeStyle = SELECTED_FILL_STYLE;
        }
        ctx.stroke(annotationRectangle);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
        ctx.fill(annotationRectangle);
        if (isInManualAlignMode && manualAlignMode.masterAnchorPoint) {
          const anchor = manualAlignMode.masterAnchorPoint;
          if (anchor.pageNumber === this.PageNumber) {
            drawAnchorIcon(ctx, anchor.x, anchor.y);
          }
        }

        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        const isSelected = selectedAnnotations.some((targetAnnot) => targetAnnot.Id.includes(this.Id));
        if (isSelected || (this as any)?.IsHovering) {
          // using the real page rotation for the labels
          const realPageRotation = getPageRotation(instance, this.PageNumber);
          const labelText = `Crop Zone`;
          const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
          const fillStyle = isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE;
          const pageWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pageHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);
          const containerBorderWidth = isInManualAlignMode ? 2 : 1;
          drawMarkupLabel(
            ctx,
            labelText,
            this.X,
            this.Y,
            HEIGHT,
            GAP,
            WIDTH_PADDING,
            fillStyle,
            zoomLevel,
            false,
            this.Width,
            this.Height,
            realPageRotation,
            pageWidth,
            pageHeight,
            containerBorderWidth,
          );
        }
      }
    }
    return CropZoneAnnotation;
  }

  annotationsExistOutsideNewCrop(
    existingAnnotations: InputAnnotation[] | { quads: any }[],
    newCrop: Core.Annotations.Annotation,
    documentType: DocumentTypes,
  ) {
    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (!instance) return false;

    // Boundary tolerance to allow annotations on or very close to crop zone boundaries
    // Using a larger tolerance (5 points) to handle snap-to-content edge cases
    const BOUNDARY_TOLERANCE = 5;

    // Get all existing crop, manual align, and polygon zones on this page (excluding the new one)
    const existingCropZones = getAllPageRectangularCropZones(instance, newCrop.PageNumber);
    const existingPolygonZones = getAllPagePolygonZones(instance, newCrop.PageNumber);

    // Check each annotation to see if it's covered by the new crop OR any existing crop/polygon
    return existingAnnotations.some((existingAnnot) => {
      const { quads } = existingAnnot;
      const rect = quadsToDimensions(quads as Quad[]);
      if (!rect) return false;

      // Calculate the bounding box for this annotation
      const annotBounds = {
        x1: rect.X,
        y1: rect.Y,
        x2: rect.X + rect.Width,
        y2: rect.Y + rect.Height,
      };

      // Check if annotation is inside the new crop (with tolerance)
      const newCropRect = newCrop.getRect();
      const isInsideNewCrop =
        annotBounds.x1 >= newCropRect.x1 - BOUNDARY_TOLERANCE &&
        annotBounds.x2 <= newCropRect.x2 + BOUNDARY_TOLERANCE &&
        annotBounds.y1 >= newCropRect.y1 - BOUNDARY_TOLERANCE &&
        annotBounds.y2 <= newCropRect.y2 + BOUNDARY_TOLERANCE;

      if (isInsideNewCrop) return false; // Annotation is covered by new crop

      // Check if annotation is inside any existing crop zone (with tolerance)
      const isInsideExistingCrop = existingCropZones.some((cropZone) => {
        const cropRect = cropZone.getRect();
        return (
          annotBounds.x1 >= cropRect.x1 - BOUNDARY_TOLERANCE &&
          annotBounds.x2 <= cropRect.x2 + BOUNDARY_TOLERANCE &&
          annotBounds.y1 >= cropRect.y1 - BOUNDARY_TOLERANCE &&
          annotBounds.y2 <= cropRect.y2 + BOUNDARY_TOLERANCE
        );
      });

      if (isInsideExistingCrop) return false; // Annotation is covered by existing crop

      // Check if annotation is inside any existing polygon zone (with tolerance)
      const isInsideExistingPolygon = existingPolygonZones.some((polygonZone) => {
        const polygonPoints = (polygonZone as Core.Annotations.PolygonAnnotation).getPath();
        if (!polygonPoints || polygonPoints.length === 0) return false;

        // Check if all corners of the annotation are within the polygon
        const corners = [
          { x: annotBounds.x1, y: annotBounds.y1 }, // top-left
          { x: annotBounds.x2, y: annotBounds.y1 }, // top-right
          { x: annotBounds.x2, y: annotBounds.y2 }, // bottom-right
          { x: annotBounds.x1, y: annotBounds.y2 }, // bottom-left
        ];

        // Shrink corners inward to apply tolerance
        const tolerantCorners = corners.map((corner, idx) => {
          const isLeft = idx === 0 || idx === 3;
          const isTop = idx === 0 || idx === 1;
          return {
            x: corner.x + (isLeft ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
            y: corner.y + (isTop ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
          };
        });

        return tolerantCorners.every((corner) => pointInPolygon(corner, polygonPoints));
      });

      if (isInsideExistingPolygon) return false; // Annotation is covered by existing polygon

      // If annotation is not covered by new crop or any existing crop/polygon, it's outside
      return true;
    });
  }

  // called from docManager/EventHandlers
  cropZoneAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    const thisDocInputAnnotations =
      documentType === DocumentTypes.source
        ? PDFAnnotationManager.getInputAnnotations(DocumentTypes.source) || []
        : PDFAnnotationManager.getInputAnnotations(DocumentTypes.target) || [];

    if (action === AnnotationAction.ADD && annotation.getCustomData(AnnotationCustomData.drawMarkups) !== 'true') {
      const nonCropAnnotationsOnPage = thisDocInputAnnotations?.filter(
        (annot) => annot.usedTool !== PDFTronTools.CROP && annot.page === annotation.PageNumber,
      );
      // Show error and delete annotation if annotations exist outside the new crop zone
      if (
        nonCropAnnotationsOnPage?.length &&
        this.annotationsExistOutsideNewCrop(nonCropAnnotationsOnPage, annotation, documentType)
      ) {
        PDFAnnotationManager.deleteAnnotationById(documentType, annotation.Id);
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Please draw the crop around any existing markups on the page.',
            type: 'error',
          }),
        );
        return;
      }

      trackEvent({
        name: 'rectangular-crop',
        data: {
          documentType,
          pageNumber: annotation.PageNumber,
          cropCoordinates: annotation.getRect(),
        },
      });

      // Show success toast for every crop zone creation
      store.dispatch(
        app.actions.setSnackMessage({
          message: `Your crop was created! You're ready to inspect.`,
          type: 'success',
        }),
      );
      // move to back and add overlay
      const annotationManager =
        documentType === DocumentTypes.source ? this.source?.annotationManager : this.target?.annotationManager;
      annotationManager?.bringToBack(annotation);
    }

    // For DELETE actions, check if there are remaining zones (crops or polygons) on the page
    if (action === AnnotationAction.DELETE) {
      const manualAlignMode = store.getState().inspection.manualAlignMode;
      const skipCropDeleteExitManualAlign = (this as any).skipCropDeleteExitManualAlign;
      if (!skipCropDeleteExitManualAlign && manualAlignMode.isActive && manualAlignMode.cropZoneId === annotation.Id) {
        (this as any).exitManualAlignMode?.();
      }
      const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
      const allPageZones = getAllPageCropAndPolygonZones(instance, annotation.PageNumber);
      // Filter out the annotation being deleted to get truly remaining zones
      const remainingZones = allPageZones.filter((zone) => zone.Id !== annotation.Id);

      if (remainingZones.length > 0) {
        // Refresh overlay for remaining zones on this page
        // The overlay will automatically be drawn by the next zone (first by ID) due to dynamic determination
        this.refreshPageOverlay(documentType, annotation.PageNumber);
      } else {
        // No zones left on page - clear any remaining overlays
        const documentWrapper = PDFManagerFactory.getViewer(documentType);
        if (documentWrapper) {
          documentWrapper.instance.Core.documentViewer.refreshAll();
          documentWrapper.instance.Core.documentViewer.updateView();
        }
      }
    } else {
      // For ADD and MODIFY actions, refresh the page overlay
      this.addCropOverlay(documentType, annotation);
    }

    // Check if this is a reload (ADD action with drawMarkups = 'true') vs user-initiated change
    // Only autosave for user-initiated changes, not the redraw
    const shouldAutoSave = !(
      action === AnnotationAction.ADD && annotation.getCustomData(AnnotationCustomData.drawMarkups) === 'true'
    );

    // Only trigger autosave/state sync for user-initiated changes, not the programmatic redraw
    if (shouldAutoSave) {
      this.annotationMixin.updateInputAnnotations(true);
    }
  }

  createCropZoneAnnotationTools() {
    [this.source, this.target].forEach((wrapper: WebViewerWrapper | null) => {
      if (!wrapper) return;
      const { instance } = wrapper;
      class CropZoneCreateTool extends instance.Core.Tools.RectangleCreateTool {
        private crosshairManager: CrosshairManager | null = null;

        constructor(cropZoneAnnotation: any) {
          super(instance.Core.documentViewer);
          instance.Core.Tools.GenericAnnotationCreateTool.call(this, instance.Core.documentViewer, cropZoneAnnotation);
        }

        // Crosshairs are set up here rather than in the constructor so that setup is paired with the
        // cleanup in switchOut. The constructor runs on every viewer load, but switchOut only runs if
        // the tool was actually selected, so setting up there left the CrosshairManager's window
        // 'resize' listener registered forever, holding the WebViewerInstance and leaking the viewer.
        switchIn(previousTool: any) {
          super.switchIn(previousTool);

          if (!this.crosshairManager) {
            this.crosshairManager = new CrosshairManager(instance, PDFTronTools.CROP, () => {
              return true; // Always allow crosshairs
            });
            this.crosshairManager.setup();
          }
        }

        switchOut(newTool: any) {
          super.switchOut(newTool);
          // Reset the cursors (to remove effects of mouseMove event handler)
          resetCursors();

          // Cleanup crosshairs
          if (this.crosshairManager) {
            this.crosshairManager.cleanup();
            this.crosshairManager = null;
          }
        }
      }

      const toolObject = new CropZoneCreateTool(this.createCropZoneAnnotation(instance));
      const defaultMouseMoveEvent = toolObject.mouseMove.bind(toolObject);
      toolObject.mouseMove = (e) => {
        // get current page
        const currentPage = getCursorPageNumber(instance, toolObject, e);
        if (!currentPage) return; // this only happens if cursor is in the space between pages

        // check for crop zones (including ManualAlignZones)
        const cropZones = getAllPageRectangularCropZones(instance, currentPage);
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        const iframeDoc = getIframeDocument(wrapper, currentPage);
        if (!iframeDoc) return;

        // If no crop zones on page, allow free creation
        if (cropZones.length === 0) {
          const selectedTool = instance.Core.documentViewer.getToolMode();
          if (selectedTool) setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
          return;
        }

        // Align zones win when they overlap a crop, so the crop underneath does not keep the pointer.
        const containsPointer = (zone: { getRect: () => { x1: number; y1: number; x2: number; y2: number } }) =>
          cursorIsInsideCropZone(e, zone, wrapper, currentPage, true);
        const alignZones = getAllPageManualAlignZones(instance, currentPage);
        const hoveredAlign = alignZones.find(containsPointer);
        const hoveredCrop = preferredRectangularZone(
          alignZones,
          getAllPageCropZones(instance, currentPage),
          containsPointer,
        );
        const selectedCrop = cropZones.find((crop) => crop.Id === selectedAnnotation?.Id);

        // Check if we're actively creating a new annotation
        const isCreatingNewAnnotation = (toolObject as any).annotation && e.buttons === 1;

        // If actively creating a new crop, allow drawing to continue even over existing crops
        if (isCreatingNewAnnotation) {
          defaultMouseMoveEvent(e);
          return;
        }

        const isNonCropSelected =
          selectedAnnotation &&
          selectedAnnotation.ToolName !== PDFTronTools.CROP &&
          selectedAnnotation.ToolName !== PDFTronTools.POLYGON &&
          selectedAnnotation.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) !== 'true';
        const pointerIsOverOtherAlignZone = !!hoveredAlign && hoveredAlign.Id !== selectedCrop?.Id;

        if (
          hoveredCrop &&
          shouldTakeHoverSelection(hoveredCrop.Id, selectedCrop?.Id, !!isNonCropSelected, !!hoveredAlign)
        ) {
          instance.Core.annotationManager.deselectAllAnnotations();
          instance.Core.annotationManager.selectAnnotation(hoveredCrop);
        }

        // Drag the selected zone. A press on an align zone that sits on a selected crop selects that zone instead.
        if (selectedCrop && e.buttons === 1 && !selectedCrop.Locked && !pointerIsOverOtherAlignZone) {
          defaultMouseMoveEvent(e);
          return;
        }

        // Ignore other click events on crop zones
        if (e.buttons === 1 && hoveredCrop) {
          return;
        }

        // If hovering over a crop zone
        if (hoveredCrop) {
          // Set cursor based on whether the crop is locked and if it's selected
          if (hoveredCrop.Locked) {
            setCursorStyle(CursorStyle.POINTER, iframeDoc);
          } else if (selectedCrop && selectedCrop.Id === hoveredCrop.Id) {
            // When hovering over the selected crop, use default cursor (allows PDFTron to handle resize handles)
            setCursorStyle(CursorStyle.DEFAULT, iframeDoc);
          } else {
            // When hovering over unselected crop, show pointer to indicate it's clickable
            setCursorStyle(CursorStyle.POINTER, iframeDoc);
          }

          // Only call defaultMouseMoveEvent when this crop is selected (for editing behavior)
          if (selectedCrop && selectedCrop.Id === hoveredCrop.Id) {
            defaultMouseMoveEvent(e);
          }
        } else {
          // Not hovering over any crop zone
          // If a crop was selected, deselect it
          if (selectedCrop) {
            instance.Core.annotationManager.deselectAnnotation(selectedCrop);
          }
          // Allow creating new crop zones in empty areas
          const selectedTool = instance.Core.documentViewer.getToolMode();
          if (selectedTool) setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
        }
      };
      wrapper.registerTool({
        toolName: PDFTronTools.CROP,
        toolObject,
        buttonImage: '',
      });
    });
  }

  reloadCropZones(sourceCropZones: InputAnnotation[], targetCropZones: InputAnnotation[]) {
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;

    for (const webviewer of [this.source, this.target]) {
      if (!webviewer) continue;
      const annotManager = webviewer.annotationManager;
      const markups = webviewer === this.source ? sourceCropZones : targetCropZones;
      const documentType = webviewer === this.source ? DocumentTypes.source : DocumentTypes.target;
      const pagesWithCrops = new Set<number>();

      // Add all crop annotations first
      for (const markup of markups) {
        const existingAnnotation = annotManager.getAnnotationById(markup.annotationId);
        if (!existingAnnotation) {
          const annotation = new (docManager.createCropZoneAnnotation(webviewer.instance))();
          annotation.Id = markup.annotationId;
          annotation.PageNumber = markup.page;
          if (markup.cropZone && isRectangularCrop(markup.cropZone)) {
            const { x1, x2, y1, y2 } = markup.cropZone;
            annotation.setRect(new webviewer.instance.Core.Math.Rect(x1, y1, x2, y2));
          }

          annotation.ReadOnly = false;
          annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');

          annotManager.addAnnotation(annotation);
          annotManager.redrawAnnotation(annotation);
          annotManager.bringToBack(annotation);

          pagesWithCrops.add(markup.page);
        }
      }

      // Refresh overlay once per page after all crops are added
      for (const pageNumber of pagesWithCrops) {
        this.refreshPageOverlay(documentType, pageNumber, false);
      }

      if (pagesWithCrops.size > 0) {
        webviewer.instance.Core.documentViewer.refreshAll();
        webviewer.instance.Core.documentViewer.updateView();
      }
    }
    store.dispatch(inspection.actions.setCropZoneId(sourceCropZones.length + targetCropZones.length + 1));
  }

  cropZoneAnnotationSelected(documentType: DocumentTypes, _annotation: Core.Annotations.Annotation, _action: string) {
    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (instance) {
      const totalPageCount = instance.Core.documentViewer.getPageCount();
      const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
      if (!selectedAnnotation) return;

      const manualAlignMode = store.getState().inspection.manualAlignMode;
      const isInManualAlign = manualAlignMode.isActive && manualAlignMode.cropZoneId === selectedAnnotation.Id;
      const disableSnap = isInManualAlign || cropContainsAnnotation(instance, selectedAnnotation);
      const popupButtons = [this.getSnapToContent(instance, documentType, disableSnap)];
      if (totalPageCount > 1) {
        popupButtons.push(this.getApplyCropToAllPagesPopup(instance));
      }
      const sourceFile = store.getState().files.source.file;
      const targetFile = store.getState().files.target.file;
      const hasPagesBasePath = !!sourceFile.fileMetadata?.pagesBasePath && !!targetFile.fileMetadata?.pagesBasePath;
      if (hasPagesBasePath) {
        popupButtons.push(this.getManualAlignPopup(instance, documentType, false));
      }
      popupButtons.push(this.getDeleteCropZonePopup(instance));

      instance.UI.annotationPopup.update(popupButtons);
    }
  }

  getSnapToContent(instance: WebViewerInstance, documentType: DocumentTypes, disabled: boolean) {
    return {
      type: 'actionButton',
      img: '/icons/snapToContentIcon.svg',
      title: disabled ? 'Delete other markups inside the crop to use Snap to Content' : 'Snap to Content',
      dataElement: disabled ? 'snapToContentButtonDisabled' : 'snapToContentButton',
      onClick: () => {
        if (disabled) return;
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        if (!selectedAnnotation) return;
        const { x1, x2, y1, y2 } = selectedAnnotation.getRect();
        const fileId = store.getState().files[documentType].file.fileId;
        store.dispatch(
          startShapesJob(fileId, documentType, 'SnapToContent', undefined, {
            id: selectedAnnotation.Id,
            top: y1,
            right: x2,
            bottom: y2,
            left: x1,
            pageNumber: selectedAnnotation.PageNumber,
          }),
        );
      },
    };
  }

  getDeleteCropZonePopup(instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      dataElement: 'deleteCropZoneButton',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        instance.Core.annotationManager.deleteAnnotation(selectedAnnotation, { imported: false, force: true });
      },
    };
  }

  getApplyCropToAllPagesPopup(instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/applyToAllPages.svg',
      title: 'Apply to all pages',
      dataElement: 'applyCropToAllPagesButton',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations();
        this.applyCropToAllPages(instance, selectedAnnotation[0]);
        instance.Core.annotationManager.deselectAllAnnotations();
      },
    };
  }

  getManualAlignPopup(instance: WebViewerInstance, documentType: DocumentTypes, disabled: boolean) {
    return {
      type: 'actionButton',
      img: '/icons/alignToMatchIcon.svg',
      title: disabled ? 'Select Graphics Pixel Compare to enable Align to Match' : 'Align to Match',
      dataElement: disabled ? 'alignToMatchButtonDisabled' : 'alignToMatchButton',
      onClick: async () => {
        if (disabled) return;
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        if (selectedAnnotation) {
          (this as any).enterManualAlignMode(instance, selectedAnnotation, documentType);
          instance.Core.annotationManager.deselectAllAnnotations();
        }
      },
    };
  }

  applyCropToAllPages(instance: WebViewerInstance, annotation: Core.Annotations.Annotation) {
    const documentType = instance === this.source?.instance ? DocumentTypes.source : DocumentTypes.target;
    const pageCount = instance.Core.documentViewer.getPageCount();
    const annotationManager = instance.Core.documentViewer.getAnnotationManager();
    const currentPage = annotation.PageNumber;
    let skippedPages = [];
    for (let page = 1; page <= pageCount; page++) {
      // skip the current page
      if (page === currentPage) {
        this.addCropOverlay(documentType, annotation);
        continue;
      }
      // Skip page if crop zone won't fit
      const cropDimensions = annotation.getRect();
      const { width: pageWidth, height: pageHeight } = instance.Core.documentViewer.getDocument().getPageInfo(page);
      if (
        cropDimensions.x1 < 0 ||
        cropDimensions.x2 > pageWidth ||
        cropDimensions.y1 < 0 ||
        cropDimensions.y2 > pageHeight
      ) {
        skippedPages.push(page);
        continue;
      }

      const newAnnotation = new (this.createCropZoneAnnotation(instance))();
      newAnnotation.setRect(annotation.getRect());
      newAnnotation.PageNumber = page;
      newAnnotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
      annotationManager.addAnnotation(newAnnotation);
      annotationManager.redrawAnnotation(newAnnotation);
      this.addCropOverlay(documentType, newAnnotation);
    }

    this.annotationMixin.updateInputAnnotations(true);

    if (skippedPages.length) {
      store.dispatch(
        app.actions.setSnackMessage({
          message: `The cropped region could not be applied to page${
            skippedPages.length > 1 ? 's' : ''
          } ${getPageRanges(skippedPages)}.`,
          type: 'error',
        }),
      );
    } else {
      store.dispatch(
        app.actions.setSnackMessage({
          message: 'Crop has successfully been applied to all pages.',
          type: 'success',
        }),
      );
    }
  }

  refreshPageOverlay(documentType: DocumentTypes, pageNumber: number, refresh = true) {
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;

    // Use the unified refresh function that updates both crops and polygons
    updateCropAndPolygonOverlays(documentWrapper.instance, pageNumber, refresh);
  }

  addCropOverlay(documentType: DocumentTypes, cropZoneAnnotation: Core.Annotations.Annotation, refresh = true) {
    // Instead of drawing overlay on individual annotation, refresh the entire page overlay
    this.refreshPageOverlay(documentType, cropZoneAnnotation.PageNumber, refresh);
  }

  removeCropOverlay(documentType: DocumentTypes, cropZoneAnnotation: Core.Annotations.Annotation, refresh = true) {
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;

    const documentViewer = documentWrapper.instance.Core.documentViewer;
    const pageNumber = cropZoneAnnotation.PageNumber;
    const pageHeight = documentViewer.getPageHeight(pageNumber);
    const pageWidth = documentViewer.getPageWidth(pageNumber);

    if (!pageHeight || !pageWidth) return;

    // Clear the draw function for this specific crop (used during resize/move operations)
    cropZoneAnnotation.draw = (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, pageWidth, pageHeight);
      ctx.save();
    };

    if (refresh) {
      documentWrapper.instance.Core.documentViewer.refreshAll();
      documentWrapper.instance.Core.documentViewer.updateView();
    }
  }

  deleteAllCropZones(documentType: DocumentTypes): void {
    const inputAnnotations = PDFAnnotationManager.getInputAnnotations(documentType);
    const annotationManager = PDFAnnotationManager.getInstance(documentType);
    if (!inputAnnotations || !annotationManager) {
      return;
    }

    const annotationsToDelete: Core.Annotations.Annotation[] = inputAnnotations
      .filter((inputAnnot) => {
        const annotation = annotationManager.getAnnotationById(inputAnnot.annotationId);
        return annotation.getCustomData(CropZoneCustomData.cropZoneAnnotation) === 'true';
      })
      .map((inputAnnotation) => annotationManager.getAnnotationById(inputAnnotation.annotationId));

    annotationManager.deleteAnnotations(annotationsToDelete, {
      imported: true,
      force: true,
    });

    PDFManagerFactory.getPDFDocManager()?.updateInputAnnotations();
  }

  async applyPDFBox(documentType: DocumentTypes, boundingBoxes: CtfPdfBox[]) {
    if (!boundingBoxes || !boundingBoxes.length) return;
    const documentWrapper = PDFManagerFactory.getViewer(documentType);
    if (!documentWrapper) return;

    // Preparing all the managers and objects needed
    const doc = documentWrapper.instance.Core.documentViewer.getDocument();

    if (!doc) return;
    const pdfDoc = await doc.getPDFDoc();
    if (!pdfDoc) return;

    const annotationManager = documentWrapper.instance.Core.annotationManager;
    const annotsToAdd = [];
    const annotsToDelete = [];
    const updatedAnnots = [];

    // I'll use this afterwards to delete all the input annotations that are not inside the new crops
    // I'll use this array with the data so I don't have to either process all the annotations for each page inside the loop of pages
    // nor use PDFtron too much
    let newCropsDimensions: {
      x1: number;
      x2: number;
      y1: number;
      y2: number;
    }[] = [];

    const pageCount = await pdfDoc.getPageCount();

    // If all pages have the same box, CTF returns a single box in the array to be used on all pages
    const singleBoxForAllPages = boundingBoxes.length === 1 && pageCount > 1;
    const boundingBoxList = singleBoxForAllPages
      ? Array.from({ length: pageCount }, () => boundingBoxes[0])
      : boundingBoxes;

    for (let i = 0; i < boundingBoxList.length; i++) {
      const { pageNumber } = singleBoxForAllPages ? { pageNumber: i + 1 } : boundingBoxList[i];
      const { left: x1, right: x2, top: y1, bottom: y2 } = boundingBoxList[i].boundingBox;
      const box = { x1, x2, y1, y2 };

      const page = await pdfDoc.getPage(pageNumber);
      const pageHeight = await page.getPageHeight();
      const pageWidth = await page.getPageWidth();

      // Get all existing crops and polygons on the page (users can now have multiple crops per page)
      const existingCropZones = getAllPageCropZones(documentWrapper.instance, pageNumber);
      const existingPolygonZones = getAllPagePolygonZones(documentWrapper.instance, pageNumber);
      const allExistingZones = [...existingCropZones, ...existingPolygonZones];

      if (allExistingZones.length > 0) {
        // If the box is the same as the page, don't draw the box, remove all existing crops and polygons
        if (box.x1 <= 0 && box.x2 >= pageWidth && box.y1 <= 0 && box.y2 >= pageHeight) {
          annotsToDelete.push(...allExistingZones);
          store.dispatch(
            app.actions.setSnackMessage({
              message: `Crop not applied: The box is the same size as the page or doesn't exist`,
              type: 'info',
            }),
          );
        } else {
          // Delete all polygons (can't be resized to rectangular box)
          if (existingPolygonZones.length > 0) {
            annotsToDelete.push(...existingPolygonZones);
          }

          // Handle rectangular crops: keep the first one and resize it, delete the rest
          if (existingCropZones.length > 0) {
            const [firstCrop, ...restOfCrops] = existingCropZones;

            // Delete all additional rectangular crops
            if (restOfCrops.length > 0) {
              annotsToDelete.push(...restOfCrops);
            }

            // Resize the first rectangular crop to match the PDF box
            firstCrop.X = box.x1;
            firstCrop.Y = box.y1;
            firstCrop.Width = box.x2 - box.x1;
            firstCrop.Height = box.y2 - box.y1;
            updatedAnnots.push(firstCrop);

            newCropsDimensions[i] = { x1: box.x1, x2: box.x2, y1: box.y1, y2: box.y2 };
          } else {
            // Only polygons existed, create a new rectangular crop
            const newAnnotation = new (this.createCropZoneAnnotation(documentWrapper.instance))();
            newAnnotation.PageNumber = pageNumber;
            newAnnotation.X = box.x1;
            newAnnotation.Y = box.y1;
            newAnnotation.Width = box.x2 - box.x1;
            newAnnotation.Height = box.y2 - box.y1;
            annotsToAdd.push(newAnnotation);

            newCropsDimensions[i] = { x1: box.x1, x2: box.x2, y1: box.y1, y2: box.y2 };
          }
        }
      } else {
        // If the box is the same as the page, don't draw the box
        if (box.x1 <= 0 && box.x2 >= pageWidth && box.y1 <= 0 && box.y2 >= pageHeight) {
          store.dispatch(
            app.actions.setSnackMessage({
              message: `Crop not applied: The box is the same size as the page or doesn't exist`,
              type: 'info',
            }),
          );
          continue;
        } else {
          const newAnnotation = new (this.createCropZoneAnnotation(documentWrapper.instance))();
          newAnnotation.PageNumber = pageNumber;
          newAnnotation.X = box.x1;
          newAnnotation.Y = box.y1;
          newAnnotation.Width = box.x2 - box.x1;
          newAnnotation.Height = box.y2 - box.y1;
          annotsToAdd.push(newAnnotation);

          newCropsDimensions[i] = { x1: box.x1, x2: box.x2, y1: box.y1, y2: box.y2 };
        }
      }
    }

    // processing the annotations we need to delete
    annotsToDelete.forEach((annot) => {
      if (annot.ToolName === PDFTronTools.CROP) {
        // we might be deleting other markups so we need to check
        this.removeCropOverlay(documentType, annot, false);
      }
    });
    annotationManager.deleteAnnotations(annotsToDelete, { force: true });

    // processing the crop annotations we need to add
    annotationManager.addAnnotations(annotsToAdd);
    annotsToAdd.forEach((annotToAdd) => this.addCropOverlay(documentType, annotToAdd, false));

    // processing the crop annotations we resized
    updatedAnnots.forEach((annot) => {
      this.addCropOverlay(documentType, annot, false);
    });
    if (updatedAnnots.length) {
      const pdfDocMgr = PDFManagerFactory.getPDFDocManager();
      pdfDocMgr?.updateInputAnnotations();
      pdfDocMgr?.redrawInputAnnotations();
    }

    documentWrapper.instance.Core.documentViewer.refreshAll();
    documentWrapper.instance.Core.documentViewer.updateView();
  }

  async updateCropWithSnap(
    cropId: string,
    documentType: DocumentTypes,
    snapData: { pageNumer: number; boundingBox: BoundingBox },
  ) {
    if (!snapData || !snapData.boundingBox) return;
    const { left: x1, right: x2, top: y1, bottom: y2 } = snapData.boundingBox;

    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;
    const annotManager = PDFAnnotationManager.getInstance(documentType);
    const webviewer = docManager[documentType];
    if (!annotManager || !webviewer) return;
    const instance = webviewer.instance;
    const crop = annotManager.getAnnotationById(cropId);
    if (!crop) return;

    const pageNumber = crop.PageNumber;

    // Update the existing annotation in place rather than deleting and recreating
    const newRect = new instance.Core.Math.Rect(x1, y1, x2, y2);
    crop.setRect(newRect);
    crop.setCustomData('snapToContent', 'true');

    // Redraw the annotation and refresh the overlay
    annotManager.redrawAnnotation(crop);
    this.refreshPageOverlay(documentType, pageNumber, false);

    // Update the view
    instance.Core.documentViewer.refreshAll();
    instance.Core.documentViewer.updateView();

    // Trigger autosave to save the updated crop zone
    this.annotationMixin.updateInputAnnotations(true);
  }
}

export default CropZone;
